/**
 * CalculationTermsTable — seznam termínů kalkulace (layout B). Grid se sloupci Termín · Operation · Sdíl. ·
 * Cost · Sdíl. · Yield · Sdíl. · Cena 1. · Kapacita · PAX · GRP · TOT/os · Profit · Marže · Break. Každá vrstva
 * (Operation / Cost / Yield) má stavový štítek a ikonu sdílení (sdílené vs vlastní schéma). Ve výchozí variantě
 * designu je barevný jen Yield (stav i sdílení); Operation a Cost jsou tlumené. Řádek se po kliknutí vybere a
 * rozbalí na inline {@link TariffEditor}. Pouze Tailwind `phx-*` tokeny (žádné klasické CSS).
 */
import { Calendar, ChevronDown, Share2, SlidersVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { CalculationTermDto } from '@/api/generated/schemas';
import { cn } from '@/shared/lib/utils';
import { Badge, Panel } from '@/shared/ui';
import { SerieStateBadge } from '../../../detail/components/serieStateBadge';
import { formatInteger, formatOneDecimal, formatTermDate } from '../../../detail/serieFormat';
import { toSerieStateKey } from '../../serieLayers';
import type { TermMetrics, TierDraft } from '../model/calc';
import { TariffEditor } from './TariffEditor';

interface CalculationTermsTableProps {
  terms: CalculationTermDto[];
  metricsByTerm: Map<string, TermMetrics>;
  /** Rozbalený termín — je zároveň vybraný (zvýrazněný řádek, zdroj souhrnu a PAX rozkladu). */
  expandedTermId: string | null;
  onToggleTerm: (termId: string) => void;
  tariffCurrency: string;
  schemeOf: (termId: string) => 'shared' | 'own';
  tiersOf: (termId: string) => TierDraft[];
  onSchemeChange: (termId: string, scheme: 'shared' | 'own') => void;
  onTierChange: (termId: string, index: number, patch: Partial<TierDraft>) => void;
  onAddTier: (termId: string) => void;
  onRemoveTier: (termId: string, index: number) => void;
  disabled?: boolean;
}

const GRID =
  'grid grid-cols-[minmax(140px,1fr)_60px_28px_60px_28px_78px_30px_88px_74px_64px_74px_78px_90px_58px_54px] items-center gap-x-2.5';
const headCell = 'text-phx-3xs font-bold uppercase tracking-wide text-phx-text-muted';
const shareHeadCell = 'text-phx-3xs font-bold uppercase tracking-wide text-phx-text-faint';

function signColor(value: number): string {
  return value >= 0 ? 'text-phx-success' : 'text-phx-danger';
}

export function CalculationTermsTable(props: CalculationTermsTableProps) {
  const { t } = useTranslation('venua-planner');
  const { terms, metricsByTerm, expandedTermId, onToggleTerm, tariffCurrency } = props;

  return (
    <Panel
      variant="filled"
      icon="calendar"
      title={t('calculation.terms.title')}
      badge={
        <Badge tone="neutral" appearance="soft">
          {terms.length}
        </Badge>
      }
      padded={false}
    >
      <div
        className={cn(GRID, 'bg-phx-surface-2 border-phx-border-2 border-b px-3 py-1.5')}
        role="row"
      >
        <span className={headCell}>{t('calculation.terms.col.term')}</span>
        <span className={headCell}>{t('calculation.terms.col.operation')}</span>
        <span className={shareHeadCell}>{t('calculation.terms.col.share')}</span>
        <span className={headCell}>{t('calculation.terms.col.cost')}</span>
        <span className={shareHeadCell}>{t('calculation.terms.col.share')}</span>
        <span className={cn(headCell, 'text-phx-primary')}>{t('calculation.terms.col.yield')}</span>
        <span className={shareHeadCell}>{t('calculation.terms.col.share')}</span>
        <span className={cn(headCell, 'text-right')}>{t('calculation.terms.col.firstPrice')}</span>
        <span className={cn(headCell, 'text-right')}>{t('calculation.terms.col.capacity')}</span>
        <span className={cn(headCell, 'text-center')}>{t('calculation.terms.col.pax')}</span>
        <span className={cn(headCell, 'text-center')}>{t('calculation.terms.col.grp')}</span>
        <span className={cn(headCell, 'text-center')}>{t('calculation.terms.col.totPerPax')}</span>
        <span className={cn(headCell, 'text-right')}>{t('calculation.terms.col.profit')}</span>
        <span className={cn(headCell, 'text-right')}>{t('calculation.terms.col.margin')}</span>
        <span className={cn(headCell, 'text-right')}>{t('calculation.terms.col.breakeven')}</span>
      </div>

      {terms.map((term) => {
        const metrics = metricsByTerm.get(term.termGroupId);
        const expanded = term.termGroupId === expandedTermId;
        const selected = expanded;
        const scheme = props.schemeOf(term.termGroupId);
        const isOwnTariff = scheme === 'own';

        return (
          <div key={term.termGroupId}>
            <div
              role="row"
              tabIndex={0}
              onClick={() => onToggleTerm(term.termGroupId)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onToggleTerm(term.termGroupId);
                }
              }}
              className={cn(
                GRID,
                'border-phx-border hover:bg-phx-surface-2 cursor-pointer border-b px-3 py-1.5',
                selected && 'bg-phx-primary-tint'
              )}
            >
              <span className="inline-flex min-w-0 items-center gap-2">
                <ChevronDown
                  size={13}
                  className={cn(
                    'text-phx-text-muted shrink-0 transition-transform',
                    expanded ? 'rotate-0' : '-rotate-90'
                  )}
                />
                <Calendar size={14} className="text-phx-primary shrink-0" />
                <span
                  className={cn(
                    'text-phx-sm truncate whitespace-nowrap tabular-nums',
                    selected ? 'text-phx-primary font-bold' : 'text-phx-text'
                  )}
                >
                  {formatTermDate(term.dateFrom, term.dateTo)}
                </span>
              </span>

              {/* Operation — stav (tlumený text) + sdílení (tlumená ikona) */}
              <span className="text-phx-2xs text-phx-text-2 inline-flex items-center font-medium whitespace-nowrap">
                {t(`serieDetail.states.${toSerieStateKey(term.operationState)}`)}
              </span>
              <span className="text-phx-text-muted inline-flex items-center justify-start">
                {term.isOwnOperation ? (
                  <SlidersVertical size={14} aria-label={t('calculation.terms.ownOperation')} />
                ) : (
                  <Share2 size={14} aria-label={t('calculation.terms.sharedOperation')} />
                )}
              </span>

              {/* Cost — stav (tlumený text) + sdílení (tlumená ikona) */}
              <span className="text-phx-2xs text-phx-text-2 inline-flex items-center font-medium whitespace-nowrap">
                {t(`serieDetail.states.${toSerieStateKey(term.productCostState)}`)}
              </span>
              <span className="text-phx-text-muted inline-flex items-center justify-start">
                {term.isOwnCost ? (
                  <SlidersVertical size={14} aria-label={t('calculation.terms.ownCost')} />
                ) : (
                  <Share2 size={14} aria-label={t('calculation.terms.sharedCost')} />
                )}
              </span>

              {/* Yield — stav (barevný badge) + sdílení (barevná ikona) */}
              <span className="inline-flex items-center justify-start">
                <SerieStateBadge state={term.calculationState} />
              </span>
              <span className="inline-flex items-center justify-start">
                {isOwnTariff ? (
                  <span className="bg-phx-primary text-phx-primary-fg rounded-phx-sm inline-flex items-center px-1 py-0.5">
                    <SlidersVertical size={14} aria-label={t('calculation.terms.ownTariff')} />
                  </span>
                ) : (
                  <span className="text-phx-primary bg-phx-primary-tint rounded-phx-sm inline-flex items-center px-1 py-0.5">
                    <Share2 size={14} aria-label={t('calculation.terms.sharedTariff')} />
                  </span>
                )}
              </span>

              <span className="text-phx-text text-phx-xs text-right font-semibold tabular-nums">
                {metrics ? `${formatInteger(metrics.firstTierPrice)} ${tariffCurrency}` : '—'}
              </span>
              <span className="text-phx-xs text-right tabular-nums">
                <span className="text-phx-text font-semibold">{term.sold}</span>
                <span className="text-phx-text-faint"> / {term.capacity}</span>
              </span>
              <span className="text-phx-info text-phx-xs text-center font-semibold tabular-nums">
                {metrics ? formatInteger(metrics.paxUnitCost) : '—'}
              </span>
              <span className="text-phx-info text-phx-xs text-center font-semibold tabular-nums">
                {metrics ? formatInteger(metrics.groupCost) : '—'}
              </span>
              <span className="text-phx-info text-phx-xs text-center font-semibold tabular-nums">
                {metrics ? formatInteger(metrics.totPerPerson) : '—'}
              </span>
              <span
                className={cn(
                  'text-phx-xs text-right font-semibold tabular-nums',
                  metrics && signColor(metrics.profit)
                )}
              >
                {metrics ? formatInteger(metrics.profit) : '—'}
              </span>
              <span
                className={cn(
                  'text-phx-xs text-right font-semibold tabular-nums',
                  metrics && signColor(metrics.profit)
                )}
              >
                {metrics ? `${formatOneDecimal(metrics.marginPercent)} %` : '—'}
              </span>
              <span
                className={cn(
                  'text-phx-xs text-right font-semibold tabular-nums',
                  metrics?.breakeven == null ? 'text-phx-danger' : 'text-phx-primary'
                )}
              >
                {metrics == null
                  ? '—'
                  : metrics.breakeven == null
                    ? t('calculation.terms.never')
                    : metrics.breakeven}
              </span>
            </div>

            {expanded ? (
              <div className="bg-phx-surface border-phx-border-2 border-b px-3.5 py-3">
                <TariffEditor
                  scheme={scheme}
                  onSchemeChange={(next) => props.onSchemeChange(term.termGroupId, next)}
                  tiers={props.tiersOf(term.termGroupId)}
                  onTierChange={(index, patch) =>
                    props.onTierChange(term.termGroupId, index, patch)
                  }
                  onAddTier={() => props.onAddTier(term.termGroupId)}
                  onRemoveTier={(index) => props.onRemoveTier(term.termGroupId, index)}
                  tariffCurrency={tariffCurrency}
                  disabled={props.disabled}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </Panel>
  );
}
